import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { offline } from '../../lib/api';
import { lastOnline, queueChangedEvent } from './offline';
import type { QueuedRequest } from './queue';

/** Connection state and the queue of captured writes. */
export function useOfflineState() {
  const client = useQueryClient();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [pending, setPending] = useState<QueuedRequest[]>([]);
  const [servedAt, setServedAt] = useState<Date | null>(null);

  const reload = useCallback(() => {
    void offline.pending().then(setPending, () => setPending([]));
  }, []);

  const flush = useCallback(async () => {
    const result = await offline.flush().catch(() => null);
    if (result && (result.sent > 0 || result.failed > 0)) await client.invalidateQueries();
    reload();
    return result;
  }, [client, reload]);

  useEffect(() => {
    reload();
    void flush();
    const goOnline = () => {
      setOnline(true);
      setServedAt(null);
      void flush();
    };
    const goOffline = () => setOnline(false);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flush();
    };
    const onMessage = (event: MessageEvent<{ type?: string; cachedAt?: string | null }>) => {
      if (event.data?.type === 'offline-served' && event.data.cachedAt) {
        setServedAt(new Date(event.data.cachedAt));
      } else if (event.data?.type === 'flush') {
        void flush();
      }
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener(queueChangedEvent, reload);
    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener('message', onMessage);
    // Retry now and then while entries wait (the browser may not fire `online`).
    const timer = window.setInterval(() => void flush(), 60_000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener(queueChangedEvent, reload);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onMessage);
      window.clearInterval(timer);
    };
  }, [flush, reload]);

  return {
    online,
    pending,
    flush,
    /** When the data on screen is from: the cached copy, else the last answer of the server. */
    since: servedAt ?? lastOnline(),
  };
}
